import { createRef } from 'react';
import { UseQueryOptions } from 'react-query';
import { cacheableQueryOptions, useAPI } from '@/services/utils';
import { useGetUserStatus } from '@/userStatus/hooks';

type TCardResultRefetch = {
  shouldRefetchBalance?: boolean;
};

const cardIsActivatedRef = createRef<boolean>();
export const useGetCardDetail = ({
  enabled = true,
  ...reactQueryOptions
}: UseQueryOptions<any> = {}) => {
  const { isVerified } = useGetUserStatus();
  return useAPI(['card', 'getCardDetail'], {
    reactQueryOptions: {
      ...(cardIsActivatedRef.current && cacheableQueryOptions),
      initialData: !isVerified ? { status: 'UserNotVerified' } : undefined,
      enabled: enabled && isVerified,
      ...reactQueryOptions,
      onSuccess: (data) => {
        (cardIsActivatedRef as any).current = data?.status === 'Activate';
        reactQueryOptions?.onSuccess?.(data);
      },
    },
  });
};

export const useGetCardBalance = (reactQueryOptions?: UseQueryOptions<any>) =>
  useAPI(['card', 'getBalance'], {
    reactQueryOptions: { ...(reactQueryOptions || {}) },
  });

export const useGetCardDetailAndBalance = (
  reactQueryOptions?: UseQueryOptions<any>,
  enableAutoCall = false
) => {
  const cardDetailResult = useGetCardDetail(reactQueryOptions);
  const isCardActivated = cardDetailResult?.data?.status === 'Activate';

  const isUserNotVerified: boolean =
    cardDetailResult?.data?.status === 'UserNotVerified';

  const cardBalanceResult = useGetCardBalance({
    ...reactQueryOptions,
    ...{ enabled: isCardActivated && enableAutoCall },
  });
  const isCardDisabled: boolean =
    `${cardBalanceResult?.error?.code}` === 'UserSessionExpire' ||
    cardDetailResult?.data?.isTokenExpired === true;

  const isLoading: boolean =
    cardDetailResult.isLoading || cardBalanceResult.isLoading;

  const isFetching: boolean =
    cardDetailResult.isFetching || cardBalanceResult.isFetching;

  const isLoadingOrFetching: boolean = isLoading || isFetching;

  const isCardCompletelyReady: boolean =
    !isLoading && isCardActivated && !isCardDisabled;

  const isError: boolean =
    !isLoadingOrFetching &&
    (_checkError(cardDetailResult?.error?.code, ['UserNotFound']) ||
      _checkError(cardBalanceResult?.error?.code, []));

  const refetch = async (
    data: TCardResultRefetch = {
      shouldRefetchBalance: true,
    }
  ) => {
    if (isUserNotVerified) return;
    cardDetailResult.refetch().then(() => {
      if (data.shouldRefetchBalance) cardBalanceResult.refetch();
    });
  };

  return {
    cardDetailResult,
    cardBalanceResult,
    isCardActivated,
    isCardDisabled,
    isCardCompletelyReady,
    isUserNotVerified,
    isError,
    isLoading,
    isFetching,
    isLoadingOrFetching,
    refetch,
  };
};

const _checkError = (
  errorCode: string | number | undefined,
  whiteList: string[]
) => !!errorCode && !whiteList.includes(`${errorCode}`);
